/**
 * Wave AP — Auto-Sim platform enhancements (UI components).
 *
 * Implements FIX 2 · 3 · 4 · 6 · 8 from
 *   /app/docs/Capexara Process Industry_AutoSim_Platform_Enhancements.md
 *
 * All components are pure presentational (no network) except:
 *   • ProductQualityTable — fetches /simulation/cases/{run_id}/product-quality-check
 *     the first time it mounts.
 *   • GroupedAdjustablePanel — fetches /simulation/adjustable-params-schema
 *
 * Styles follow the palette used by AutoSimWorkspace
 * (--pf-surface / --pf-border / JetBrains Mono for numerics).
 */
import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, XCircle, Info, Download, ChevronLeft, ChevronRight, GitBranch, AlertCircle, Sparkles, FileSpreadsheet, ArrowUp, ArrowDown } from "lucide-react";
import api from "../lib/api";

const mono = { fontFamily: "'JetBrains Mono', monospace" };

/* ─────────────────────────────────────────────────────────────────────
 * FIX 3 · Product Quality Table — color-coded PASS / FAIL / INFO
 *         + [Apply Fix] buttons that translate suggestions into
 *           concrete adjustable-parameter patches.
 * ────────────────────────────────────────────────────────────────── */
export function ProductQualityTable({ projectId, runId, qualityRaw, onApplyFix }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [specSource, setSpecSource] = useState("design_basis");

  useEffect(() => {
    let alive = true;
    async function load() {
      if (!projectId || !runId) return;
      setLoading(true); setErr("");
      try {
        const r = await api.post(
          `/projects/${projectId}/simulation/cases/${runId}/product-quality-check`
        );
        if (alive) {
          setRows(r.data?.rows || []);
          setSpecSource(r.data?.spec_source || "design_basis");
        }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || e.message);
      } finally { if (alive) setLoading(false); }
    }
    load();
    return () => { alive = false; };
  }, [projectId, runId]);

  const passCount = (rows || []).filter((r) => r.status === "PASS").length;
  const failCount = (rows || []).filter((r) => r.status === "FAIL").length;
  const infoCount = (rows || []).filter((r) => r.status === "INFO").length;

  if (loading) return <div style={{ color: "var(--pf-text-muted)", padding: 16, fontSize: 12 }}>Checking against specs…</div>;
  if (err) return <div style={{ color: "#f87171", padding: 12, fontSize: 12 }}>Error: {err}</div>;
  if (!rows || rows.length === 0) {
    // No wizard specs to compare — fall back to a simple numeric dump
    return (
      <div data-testid="autosim-quality-fallback" style={{ color: "var(--pf-text-muted)", fontSize: 11 }}>
        <div style={{ marginBottom: 8 }}>No product specifications found in the Design Basis (A.1). Showing measured values only:</div>
        <table style={{ width: "100%", fontSize: 11, ...mono, borderCollapse: "collapse" }}>
          <tbody>
            {Object.entries(qualityRaw || {}).map(([k, v]) => (
              <tr key={k} style={{ borderBottom: "1px solid var(--pf-border)" }}>
                <td style={{ padding: 6, color: "var(--pf-text-muted)" }}>{k}</td>
                <td style={{ padding: 6, color: "var(--pf-text)", textAlign: "right" }}>{String(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div data-testid="autosim-quality-table">
      {specSource === "template_fallback" && (
        <div data-testid="autosim-quality-source-banner"
              style={{ display: "flex", alignItems: "center", gap: 8,
                        padding: "8px 12px", marginBottom: 10,
                        background: "rgba(245,158,11,0.10)",
                        border: "1px solid rgba(245,158,11,0.35)",
                        borderRadius: 6, fontSize: 11.5,
                        color: "#fbbf24", ...mono }}>
          <Info size={14} />
          <span>
            Specs sourced from your project's <b>template defaults</b> — define
            them in <b>Design Basis A.1 → Products</b> to override with
            project-specific values.
          </span>
        </div>
      )}
      <div style={{ display: "flex", gap: 10, marginBottom: 10, ...mono, fontSize: 11.5 }}>
        <span style={{ color: "#22c55e" }}>✓ {passCount} PASS</span>
        <span style={{ color: failCount ? "#ef4444" : "var(--pf-text-muted)" }}>✗ {failCount} FAIL</span>
        <span style={{ color: "var(--pf-text-muted)" }}>ℹ {infoCount} INFO</span>
      </div>
      <table data-testid="autosim-quality-spec-table" style={{ width: "100%", fontSize: 11.5, ...mono, borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "var(--pf-surface-2)", color: "var(--pf-text-muted)" }}>
            <th style={{ padding: 7, textAlign: "left" }}>Product</th>
            <th style={{ padding: 7, textAlign: "left" }}>Parameter</th>
            <th style={{ padding: 7, textAlign: "right" }}>Spec</th>
            <th style={{ padding: 7, textAlign: "right" }}>Actual</th>
            <th style={{ padding: 7, textAlign: "center" }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const bg =
              r.status === "PASS" ? "rgba(34,197,94,0.08)" :
              r.status === "FAIL" ? "rgba(239,68,68,0.12)" : "transparent";
            const color =
              r.status === "PASS" ? "#4ade80" :
              r.status === "FAIL" ? "#f87171" : "var(--pf-text-muted)";
            const Icon = r.status === "PASS" ? CheckCircle2 : r.status === "FAIL" ? XCircle : Info;
            return (
              <React.Fragment key={i}>
                <tr style={{ background: bg, borderBottom: "1px solid var(--pf-border)" }}>
                  <td style={{ padding: 7, color: "var(--pf-text)" }}>{r.product}</td>
                  <td style={{ padding: 7, color: "var(--pf-text)" }}>{r.parameter}</td>
                  <td style={{ padding: 7, color: "var(--pf-text-muted)", textAlign: "right" }}>{r.specification}</td>
                  <td style={{ padding: 7, color: "var(--pf-text)", textAlign: "right", fontWeight: 600 }}>{r.actual}</td>
                  <td style={{ padding: 7, textAlign: "center" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color, fontWeight: 700 }}>
                      <Icon size={12} /> {r.status}
                    </span>
                  </td>
                </tr>
                {r.suggestion && (
                  <tr style={{ background: bg }}>
                    <td colSpan={5} style={{ padding: "3px 24px 9px 24px", color: "#fbbf24", fontSize: 10.5, fontStyle: "italic" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span>→ {r.suggestion}</span>
                        {r.status === "FAIL" && onApplyFix && (
                          <button
                            data-testid={`autosim-apply-fix-quality-${i}`}
                            onClick={() => onApplyFix(r)}
                            style={{
                              padding: "3px 10px", fontSize: 10.5, ...mono,
                              background: "rgba(59,130,246,0.15)", border: "1px solid #3b82f6",
                              color: "#7dd3fc", borderRadius: 3, cursor: "pointer",
                              display: "inline-flex", alignItems: "center", gap: 4, fontStyle: "normal",
                            }}
                          >
                            <Sparkles size={10} /> Apply Fix
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * FIX 8 · Equipment Duties — horizontal bar chart
 * ────────────────────────────────────────────────────────────────── */
export function DutiesBarChart({ duties }) {
  const rows = (duties || []).map((d) => ({
    tag: d.tag || d.code || "?",
    name: d.name || "",
    duty: Number(d.duty_kW ?? d.duty_kw ?? d.duty ?? 0),
    type: (d.type || (Number(d.duty_kW ?? d.duty ?? 0) >= 0 ? "heating" : "cooling")).toLowerCase(),
    utility: d.utility || d.utility_type || "",
  }));
  if (rows.length === 0) return (
    <div data-testid="autosim-duties-empty" style={{ color: "var(--pf-text-muted)", fontSize: 12, padding: 16, textAlign: "center" }}>
      No equipment duties recorded for this case.
    </div>
  );

  const max = Math.max(...rows.map((r) => Math.abs(r.duty)), 1);
  const totalHeat = rows.filter((r) => r.duty >= 0).reduce((s, r) => s + r.duty, 0);
  const totalCool = rows.filter((r) => r.duty < 0).reduce((s, r) => s + r.duty, 0);

  return (
    <div data-testid="autosim-duties-barchart">
      <table style={{ width: "100%", fontSize: 11.5, ...mono, borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "var(--pf-surface-2)", color: "var(--pf-text-muted)" }}>
            <th style={{ padding: 7, textAlign: "left", width: 60 }}>Tag</th>
            <th style={{ padding: 7, textAlign: "left" }}>Name</th>
            <th style={{ padding: 7, textAlign: "right", width: 100 }}>Duty (kW)</th>
            <th style={{ padding: 7, textAlign: "left", width: 80 }}>Type</th>
            <th style={{ padding: 7, textAlign: "left", width: 90 }}>Utility</th>
            <th style={{ padding: 7, textAlign: "left" }}>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const pct = Math.min(100, (Math.abs(r.duty) / max) * 100);
            const color = r.type.includes("cool") || r.duty < 0 ? "#3b82f6" : "#ef4444";
            return (
              <tr key={i} style={{ borderBottom: "1px solid var(--pf-border)" }}>
                <td style={{ padding: 6, color: "var(--pf-text)" }}>{r.tag}</td>
                <td style={{ padding: 6, color: "var(--pf-text-muted)" }}>{r.name}</td>
                <td style={{ padding: 6, color: "var(--pf-text)", textAlign: "right", fontWeight: 600 }}>
                  {r.duty >= 0 ? "+" : ""}{r.duty.toFixed(0)}
                </td>
                <td style={{ padding: 6, color: "var(--pf-text-muted)" }}>{r.type || "—"}</td>
                <td style={{ padding: 6, color: "var(--pf-text-muted)" }}>{r.utility || "—"}</td>
                <td style={{ padding: 6 }}>
                  <div style={{
                    height: 10, width: `${pct}%`, background: color, opacity: 0.8,
                    borderRadius: 2, minWidth: 2,
                  }} data-testid={`autosim-duty-bar-${r.tag}`} />
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: "2px solid var(--pf-border)", background: "var(--pf-surface-2)" }}>
            <td colSpan={2} style={{ padding: 7, color: "var(--pf-text-muted)" }}>TOTALS</td>
            <td style={{ padding: 7, color: "#ef4444", textAlign: "right", fontWeight: 700 }}>
              Heat: +{totalHeat.toFixed(0)}
            </td>
            <td colSpan={3} style={{ padding: 7, color: "#3b82f6", fontWeight: 700 }}>
              Cool: {totalCool.toFixed(0)} · Net: {(totalHeat + totalCool).toFixed(0)} kW
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * FIX 6 · Stream Table — proper H&MB format, paginated, sortable,
 *         BL-highlighted, with click-to-drill-down and Excel export.
 * ────────────────────────────────────────────────────────────────── */
export function StreamHMBTable({ streams }) {
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState({ key: "stream_id", dir: "asc" });
  const [detail, setDetail] = useState(null);
  const perPage = 6;

  const all = useMemo(() => {
    const arr = (streams || []).map((s, i) => ({
      ...s,
      _idx: i,
      _name: s.name || s.stream_id || `S-${String(i + 1).padStart(3, "0")}`,
      _from: s.from || s.source_tag || "BL",
      _to: s.to || s.target_tag || "BL",
      _T: Number(s.T ?? s.temperature_c ?? s.temp_c ?? NaN),
      _flow: Number(s.flow_kgph ?? s.mass_flow ?? s.flow_kgh ?? s.flow ?? NaN),
    }));
    const keyFn = {
      stream_id: (s) => s._name,
      from: (s) => s._from,
      T: (s) => (isFinite(s._T) ? s._T : -Infinity),
      flow: (s) => (isFinite(s._flow) ? s._flow : -Infinity),
    }[sort.key] || ((s) => s._idx);
    const sorted = [...arr].sort((a, b) => {
      const av = keyFn(a), bv = keyFn(b);
      if (av === bv) return 0;
      const cmp = av < bv ? -1 : 1;
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [streams, sort]);

  const totalPages = Math.max(1, Math.ceil(all.length / perPage));
  const pageStreams = all.slice(page * perPage, (page + 1) * perPage);
  // Reset page if sort brings it out of range
  useEffect(() => { if (page >= totalPages) setPage(0); }, [page, totalPages]);

  if (all.length === 0) return (
    <div data-testid="autosim-streams-empty" style={{ color: "var(--pf-text-muted)", fontSize: 12, padding: 16, textAlign: "center" }}>
      No streams produced for this case.
    </div>
  );

  // Full H&MB row definitions — now 11 rows matching engineering convention
  const rowDefs = [
    { key: "temperature_c",  label: "Temperature °C",  get: (s) => s.T ?? s.temperature_c ?? s.temp_c },
    { key: "pressure_barg",  label: "Pressure barg",   get: (s) => s.P ?? s.pressure_barg ?? s.press_bar },
    { key: "flow_kgph",      label: "Mass Flow kg/h",  get: (s) => s.flow_kgph ?? s.mass_flow ?? s.flow_kgh ?? s.flow },
    { key: "vol_flow_m3ph",  label: "Vol. Flow m³/h",  get: (s) => s.vol_flow_m3ph ?? s.volumetric_flow ?? s.vol_flow
                                                                  ?? ((s.flow_kgph ?? s.mass_flow) && (s.density_kgm3 ?? s.density)
                                                                        ? (s.flow_kgph ?? s.mass_flow) / (s.density_kgm3 ?? s.density) : undefined) },
    { key: "phase",          label: "Phase",           get: (s) => s.phase || "—" },
    { key: "vapor_frac",     label: "Vapor Frac.",     get: (s) => s.vapor_frac ?? s.vapor_fraction ?? (s.phase === "vapor" ? 1 : s.phase === "liquid" ? 0 : undefined) },
    { key: "density",        label: "Density kg/m³",   get: (s) => s.density_kgm3 ?? s.density },
    { key: "viscosity",      label: "Viscosity cP",    get: (s) => s.viscosity_cp ?? s.viscosity ?? s.mu_cp },
    { key: "mw",             label: "Mol. Weight",     get: (s) => s.mw ?? s.molecular_weight },
    { key: "cp",             label: "Sp. Heat kJ/kgK", get: (s) => s.cp_kjkgk ?? s.cp ?? s.specific_heat },
    { key: "enthalpy",       label: "Enthalpy kJ/kg",  get: (s) => s.enthalpy_kjkg ?? s.enthalpy },
  ];

  const fmt = (v) => {
    if (v === undefined || v === null || v === "") return "—";
    if (typeof v === "number") {
      if (!isFinite(v)) return "—";
      return (Math.abs(v) >= 1000 ? v.toFixed(0) : v.toFixed(2));
    }
    return String(v);
  };

  // BL highlight — feed streams (from BL) green, product streams (to BL) red
  const bgFor = (s) => {
    if ((s._from || "").toUpperCase() === "BL") return "rgba(34,197,94,0.08)";
    if ((s._to || "").toUpperCase() === "BL")   return "rgba(239,68,68,0.08)";
    return "transparent";
  };

  const exportCSV = () => {
    const header = ["Property", ...all.map((s) => s._name)].join(",");
    const fromRow = ["From",    ...all.map((s) => s._from)].join(",");
    const toRow   = ["To",      ...all.map((s) => s._to)].join(",");
    const lines = rowDefs.map((rd) => [rd.label, ...all.map((s) => fmt(rd.get(s)))].join(","));
    const blob = new Blob([[header, fromRow, toRow, ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "stream_hmb.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const exportXLSX = () => {
    // Simple HTML-table-as-Excel — Excel opens .xls natively from HTML.
    const html = [
      `<html><head><meta charset="utf-8"><title>Stream H&amp;MB</title></head><body>`,
      `<table border="1" cellspacing="0" cellpadding="4">`,
      `<tr><th>Property</th>${all.map((s) => `<th>${s._name}</th>`).join("")}</tr>`,
      `<tr><td>From</td>${all.map((s) => `<td>${s._from}</td>`).join("")}</tr>`,
      `<tr><td>To</td>${all.map((s) => `<td>${s._to}</td>`).join("")}</tr>`,
      ...rowDefs.map((rd) => `<tr><td>${rd.label}</td>${all.map((s) => `<td>${fmt(rd.get(s))}</td>`).join("")}</tr>`),
      `</table></body></html>`,
    ].join("");
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "stream_hmb.xls"; a.click();
    URL.revokeObjectURL(url);
  };

  const SortBtn = ({ k, label }) => (
    <button
      data-testid={`autosim-streams-sort-${k}`}
      onClick={() => setSort({ key: k, dir: sort.key === k && sort.dir === "asc" ? "desc" : "asc" })}
      style={{ padding: "3px 7px", background: sort.key === k ? "rgba(59,130,246,0.15)" : "transparent",
               border: `1px solid ${sort.key === k ? "#3b82f6" : "var(--pf-border)"}`,
               color: sort.key === k ? "#7dd3fc" : "var(--pf-text-muted)",
               borderRadius: 2, cursor: "pointer", fontSize: 10, ...mono,
               display: "inline-flex", alignItems: "center", gap: 3 }}
    >
      {label}
      {sort.key === k && (sort.dir === "asc" ? <ArrowUp size={9} /> : <ArrowDown size={9} />)}
    </button>
  );

  return (
    <div data-testid="autosim-streams-hmb">
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, ...mono, fontSize: 11, flexWrap: "wrap" }}>
        <span style={{ color: "var(--pf-text-muted)" }}>Sort:</span>
        <SortBtn k="stream_id" label="#" />
        <SortBtn k="from" label="From" />
        <SortBtn k="T" label="T" />
        <SortBtn k="flow" label="Flow" />
        <div style={{ width: 1, height: 18, background: "var(--pf-border)", margin: "0 4px" }} />
        <span style={{ color: "var(--pf-text-muted)" }}>Streams {page * perPage + 1}–{Math.min((page + 1) * perPage, all.length)} of {all.length}</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 9, color: "#4ade80", padding: "2px 6px", background: "rgba(34,197,94,0.12)", borderRadius: 2 }}>■ Feed (BL→)</span>
        <span style={{ fontSize: 9, color: "#f87171", padding: "2px 6px", background: "rgba(239,68,68,0.12)", borderRadius: 2 }}>■ Product (→BL)</span>
        <button
          data-testid="autosim-streams-prev"
          onClick={() => setPage(Math.max(0, page - 1))}
          disabled={page === 0}
          style={{ padding: "4px 8px", background: "transparent", border: "1px solid var(--pf-border)",
                   color: page === 0 ? "var(--pf-text-muted)" : "var(--pf-text)", borderRadius: 3,
                   cursor: page === 0 ? "not-allowed" : "pointer", fontSize: 11 }}
        ><ChevronLeft size={12} /></button>
        <button
          data-testid="autosim-streams-next"
          onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          style={{ padding: "4px 8px", background: "transparent", border: "1px solid var(--pf-border)",
                   color: page >= totalPages - 1 ? "var(--pf-text-muted)" : "var(--pf-text)", borderRadius: 3,
                   cursor: page >= totalPages - 1 ? "not-allowed" : "pointer", fontSize: 11 }}
        ><ChevronRight size={12} /></button>
        <button
          data-testid="autosim-streams-export-csv"
          onClick={exportCSV}
          style={{ padding: "4px 8px", background: "transparent", border: "1px solid var(--pf-border)",
                   color: "var(--pf-text)", borderRadius: 3, cursor: "pointer", fontSize: 11,
                   display: "flex", alignItems: "center", gap: 4 }}
        ><Download size={11} /> CSV</button>
        <button
          data-testid="autosim-streams-export-xlsx"
          onClick={exportXLSX}
          style={{ padding: "4px 8px", background: "rgba(34,197,94,0.15)", border: "1px solid #22c55e",
                   color: "#86efac", borderRadius: 3, cursor: "pointer", fontSize: 11,
                   display: "flex", alignItems: "center", gap: 4 }}
        ><FileSpreadsheet size={11} /> Excel</button>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ minWidth: "100%", fontSize: 11, ...mono, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--pf-surface-2)" }}>
              <th style={{ padding: 7, textAlign: "left", color: "var(--pf-text-muted)", position: "sticky", left: 0, background: "var(--pf-surface-2)", zIndex: 1 }}>Property</th>
              {pageStreams.map((s, i) => (
                <th key={i} style={{ padding: 7, textAlign: "right", color: "var(--pf-text)", minWidth: 88, background: bgFor(s) }}>
                  <button
                    data-testid={`autosim-stream-header-${s._name}`}
                    onClick={() => setDetail(s)}
                    style={{ background: "transparent", border: 0, color: "var(--pf-text)", cursor: "pointer",
                             ...mono, fontSize: 11, fontWeight: 600, textAlign: "right", width: "100%" }}
                    title="Click for full stream detail"
                  >
                    {s._name}
                  </button>
                  <div style={{ fontSize: 9, color: "var(--pf-text-muted)", fontWeight: 400, marginTop: 2 }}>
                    {s._from} → {s._to}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowDefs.map((rd) => (
              <tr key={rd.key} style={{ borderBottom: "1px solid var(--pf-border)" }}>
                <td style={{ padding: 7, color: "var(--pf-text-muted)", position: "sticky", left: 0, background: "var(--pf-surface)", zIndex: 1 }}>
                  {rd.label}
                </td>
                {pageStreams.map((s, i) => (
                  <td key={i} style={{ padding: 7, textAlign: "right", color: "var(--pf-text)", background: bgFor(s) }}>
                    {fmt(rd.get(s))}
                  </td>
                ))}
              </tr>
            ))}
            {(() => {
              const firstComp = pageStreams[0]?.composition || pageStreams[0]?.comp || {};
              const compKeys = Object.keys(
                typeof firstComp === "object" && !Array.isArray(firstComp) ? firstComp : {}
              ).slice(0, 10);
              if (compKeys.length === 0) return null;
              return (
                <>
                  <tr><td colSpan={pageStreams.length + 1} style={{ padding: "8px 7px 4px 7px", color: "var(--pf-text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>Composition (mol fraction)</td></tr>
                  {compKeys.map((k) => (
                    <tr key={k} style={{ borderBottom: "1px solid var(--pf-border)" }}>
                      <td style={{ padding: "5px 7px", color: "var(--pf-text-muted)", position: "sticky", left: 0, background: "var(--pf-surface)" }}>{k}</td>
                      {pageStreams.map((s, i) => (
                        <td key={i} style={{ padding: "5px 7px", textAlign: "right", color: "var(--pf-text)", background: bgFor(s) }}>
                          {fmt(((s.composition || s.comp || {})[k]))}
                        </td>
                      ))}
                    </tr>
                  ))}
                </>
              );
            })()}
          </tbody>
        </table>
      </div>

      {detail && <StreamDetailModal stream={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function StreamDetailModal({ stream, onClose }) {
  const entries = Object.entries(stream).filter(([k]) => !k.startsWith("_"));
  return (
    <div data-testid="autosim-stream-detail" onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "var(--pf-surface)", border: "1px solid var(--pf-border)",
        borderRadius: 6, padding: 18, width: 520, maxHeight: "80vh", overflow: "auto",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <b style={{ fontSize: 14, ...mono, color: "var(--pf-text)" }}>
            Stream {stream._name}
          </b>
          <span style={{ color: "var(--pf-text-muted)", fontSize: 11 }}>
            {stream._from} → {stream._to}
          </span>
          <div style={{ flex: 1 }} />
          <button data-testid="autosim-stream-detail-close" onClick={onClose}
            style={{ background: "transparent", border: 0, color: "var(--pf-text-muted)", cursor: "pointer", fontSize: 18 }}>×</button>
        </div>
        <table style={{ width: "100%", fontSize: 11.5, ...mono, borderCollapse: "collapse" }}>
          <tbody>
            {entries.map(([k, v]) => (
              <tr key={k} style={{ borderBottom: "1px solid var(--pf-border)" }}>
                <td style={{ padding: "5px 8px", color: "var(--pf-text-muted)", width: "40%" }}>{k}</td>
                <td style={{ padding: "5px 8px", color: "var(--pf-text)", wordBreak: "break-word" }}>
                  {typeof v === "object" ? JSON.stringify(v, null, 2) : String(v)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * FIX 1 · Branch Status Display — per-branch converged / failed status
 * ────────────────────────────────────────────────────────────────── */
export function BranchStatusPanel({ projectId, runId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const runSolver = async () => {
    if (!projectId || !runId) return;
    setLoading(true); setErr("");
    try {
      const r = await api.post(`/projects/${projectId}/simulation/cases/${runId}/solve-flowsheet`);
      setReport(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { setReport(null); }, [runId]);

  if (!report) {
    return (
      <div data-testid="autosim-branches-empty" style={{ padding: 16, border: "1px dashed var(--pf-border)", borderRadius: 4, textAlign: "center" }}>
        <div style={{ color: "var(--pf-text-muted)", fontSize: 12, marginBottom: 8 }}>
          Branch-isolation solver not yet run for this case.
        </div>
        <button
          data-testid="autosim-run-solver-btn"
          onClick={runSolver} disabled={loading}
          style={{ padding: "6px 14px", background: "rgba(59,130,246,0.15)", border: "1px solid #3b82f6",
                    color: "#7dd3fc", borderRadius: 3, cursor: loading ? "wait" : "pointer",
                    fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <GitBranch size={12} /> {loading ? "Solving…" : "Run branch-isolation solver"}
        </button>
        {err && <div style={{ color: "#f87171", fontSize: 11, marginTop: 8 }}>Error: {err}</div>}
      </div>
    );
  }

  const converged = report.converged_branches || [];
  const failed = report.failed_branches || [];
  const total = (report.branches || []).length;

  return (
    <div data-testid="autosim-branches" style={{ ...mono, fontSize: 11.5 }}>
      <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
        <span style={{ color: "#4ade80", fontWeight: 700 }}>✓ {converged.length} CONVERGED</span>
        <span style={{ color: failed.length ? "#f87171" : "var(--pf-text-muted)", fontWeight: 700 }}>✗ {failed.length} FAILED</span>
        <span style={{ color: "var(--pf-text-muted)" }}>· {total} branches total · {report.converged_equipment?.length || 0}/{(report.converged_equipment?.length || 0) + (report.failed_equipment?.length || 0)} equipment</span>
      </div>
      {report.branches.map((b, i) => (
        <div key={i} data-testid={`autosim-branch-${b.name}`} style={{
          padding: 10, marginBottom: 8, borderRadius: 4,
          background: b.status === "converged" ? "rgba(34,197,94,0.07)" : "rgba(239,68,68,0.10)",
          border: `1px solid ${b.status === "converged" ? "#22c55e50" : "#ef444450"}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            {b.status === "converged"
              ? <CheckCircle2 size={12} color="#4ade80" />
              : <AlertCircle size={12} color="#f87171" />}
            <b style={{ color: b.status === "converged" ? "#4ade80" : "#f87171" }}>{b.name}</b>
            <span style={{ color: "var(--pf-text-muted)", fontSize: 10 }}>
              — {b.equipment_tags?.length || 0} equipment · {b.iterations || 0} iter
            </span>
          </div>
          <div style={{ fontSize: 10.5, color: "var(--pf-text-muted)", marginLeft: 18 }}>
            Equipment: {(b.equipment_tags || []).join(", ")}
          </div>
          {b.status === "failed" && b.root_cause_tag && (
            <div style={{ fontSize: 10.5, marginLeft: 18, marginTop: 4, color: "#fbbf24" }}>
              Root cause: <b>{b.root_cause_tag}</b> — {b.error}
            </div>
          )}
          {b.recycles?.length > 0 && (
            <div style={{ fontSize: 10.5, marginLeft: 18, marginTop: 4, color: "var(--pf-text-muted)" }}>
              Recycles: {b.recycles.length} · Tears: {b.tear_streams?.length || 0}
            </div>
          )}
        </div>
      ))}
      {report.partial_results_available && failed.length > 0 && (
        <div style={{ padding: 8, background: "rgba(251,191,36,0.10)", border: "1px solid #fbbf2450",
                       borderRadius: 3, fontSize: 11, color: "#fde68a", marginTop: 8 }}>
          ℹ Partial results available — streams / duties for the {converged.length} converged branch{converged.length === 1 ? "" : "es"} are still valid.
        </div>
      )}
      <button
        data-testid="autosim-rerun-solver-btn"
        onClick={runSolver} disabled={loading}
        style={{ marginTop: 8, padding: "5px 12px", background: "transparent", border: "1px solid var(--pf-border)",
                  color: "var(--pf-text)", borderRadius: 3, cursor: "pointer", fontSize: 11,
                  display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        <GitBranch size={11} /> Re-run solver
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * FIX 2 · Grouped Adjustable Panel — auto-generated from equipment list
 * ────────────────────────────────────────────────────────────────── */
export function GroupedAdjustablePanel({ projectId, currentAdjustable, onChange }) {
  const [groups, setGroups] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/projects/${projectId}/simulation/adjustable-params-schema`);
        if (alive) setGroups(r.data?.groups || []);
      } catch (e) { if (alive) setErr(e?.response?.data?.detail || e.message); }
    })();
    return () => { alive = false; };
  }, [projectId]);

  if (err) return <div style={{ color: "#f87171", fontSize: 11 }}>{err}</div>;
  if (!groups) return <div style={{ color: "var(--pf-text-muted)", fontSize: 11 }}>Loading equipment parameters…</div>;
  if (groups.length === 0) {
    return (
      <div data-testid="autosim-adjustable-empty" style={{ color: "var(--pf-text-muted)", fontSize: 11, padding: 8, fontStyle: "italic" }}>
        No equipment list loaded yet — the flat adjustable panel (top-pressure, reflux, num-trays) is shown on the main panel. Run B.1 to populate the equipment list and this panel will auto-generate column, heater, compressor, vessel and air-cooler parameters.
      </div>
    );
  }

  return (
    <div data-testid="autosim-grouped-adjustable" style={{ ...mono, fontSize: 11 }}>
      {groups.map((g) => {
        const isOpen = expanded[g.tag] !== false;
        return (
          <div key={g.tag} data-testid={`autosim-group-${g.tag}`} style={{ marginBottom: 6, border: "1px solid var(--pf-border)", borderRadius: 3 }}>
            <button
              onClick={() => setExpanded({ ...expanded, [g.tag]: !isOpen })}
              data-testid={`autosim-group-toggle-${g.tag}`}
              style={{ width: "100%", padding: "6px 10px", background: "var(--pf-surface-2)",
                        border: 0, color: "var(--pf-text)", cursor: "pointer", textAlign: "left",
                        display: "flex", alignItems: "center", gap: 6, fontSize: 11, ...mono }}
            >
              <span style={{ color: "#3b82f6" }}>{isOpen ? "▼" : "▶"}</span>
              <b>{g.tag}</b>
              <span style={{ color: "var(--pf-text-muted)" }}>{g.name}</span>
              <span style={{ flex: 1 }} />
              <span style={{ color: "var(--pf-text-muted)", fontSize: 10 }}>{g.type} · {g.params.length}</span>
            </button>
            {isOpen && (
              <div style={{ padding: 8 }}>
                {g.params.map((p) => (
                  <div key={p.key} style={{ display: "grid", gridTemplateColumns: "1fr 80px 40px",
                                              gap: 4, alignItems: "center", marginBottom: 3 }}>
                    <label style={{ color: "var(--pf-text-muted)", fontSize: 10.5 }}>{p.label}</label>
                    <input
                      data-testid={`autosim-param-${p.key}`}
                      type="number"
                      value={(currentAdjustable || {})[p.key] ?? p.default ?? ""}
                      onChange={(e) => onChange(p.key, e.target.value === "" ? "" : Number(e.target.value))}
                      style={{ padding: "3px 6px", background: "var(--pf-surface-3, rgba(255,255,255,0.04))",
                                border: "1px solid var(--pf-border)", color: "var(--pf-text)",
                                borderRadius: 2, fontSize: 10.5, ...mono, textAlign: "right" }}
                    />
                    <span style={{ color: "var(--pf-text-muted)", fontSize: 10 }}>{p.unit}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const _all = { ProductQualityTable, DutiesBarChart, StreamHMBTable, BranchStatusPanel, GroupedAdjustablePanel };
export default _all;
